import { useState } from 'react'
import { motion } from 'framer-motion'
import { Bell, X, Check, AlertTriangle } from 'lucide-react'
import { Button } from './Button'

interface PushOptInDialogProps {
  /** Backdrop, X and "Agora não" — the row stays put for next time. */
  onClose: () => void
  /** The hook's subscribe(); resolves false when the browser refused. */
  onSubscribe: () => Promise<boolean>
}

/**
 * The confirmation step in front of the browser's permission prompt.
 *
 * That prompt is one-shot per origin: a denial cannot be re-asked, and the only
 * way back is the OS or browser settings. So a stray tap on the Início row must
 * not spend it — this dialog says what the notifications are for, warns that
 * the next tap opens the real prompt, and makes "Permitir" the expected answer
 * before it appears.
 *
 * Closing is free and leaves the row in place; there is no snooze, because the
 * row is a to-do rather than a banner and nothing else clears it.
 *
 * Wrap in <AnimatePresence> for the exit animation. The permission request runs
 * from the "Ativar" click, which keeps it inside a user gesture — iOS
 * auto-denies anything else.
 */
export function PushOptInDialog({ onClose, onSubscribe }: PushOptInDialogProps) {
  const [status, setStatus] = useState<'idle' | 'working' | 'denied' | 'error'>('idle')

  const handleSubscribe = async () => {
    setStatus('working')
    const ok = await onSubscribe()
    if (ok) {
      onClose()
      return
    }
    setStatus(
      'Notification' in window && Notification.permission === 'denied' ? 'denied' : 'error'
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm glass-card rounded-2xl p-6"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-turquoise" />
            Ativar notificações
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {status === 'denied' || status === 'error' ? (
          <>
            <div className="flex items-start gap-2.5 mb-5">
              <AlertTriangle className="w-4 h-4 text-coral shrink-0 mt-0.5" />
              <p className="text-sm text-white/70 leading-relaxed">
                {status === 'denied'
                  ? 'Permissão negada. Para ativar, libere as notificações do BT Vision nos ajustes do navegador ou do celular.'
                  : 'Não foi possível ativar agora. Tente de novo mais tarde.'}
              </p>
            </div>
            <Button onClick={onClose} variant="secondary" fullWidth>
              Entendi
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-white/60 mb-3">Você recebe um aviso quando:</p>
            <ul className="space-y-2 mb-4">
              {[
                'a rodada for liberada e você estiver nela',
                'as inscrições da próxima rodada abrirem',
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-turquoise shrink-0 mt-0.5" strokeWidth={3} />
                  <span className="text-sm text-white/80 leading-snug">{line}</span>
                </li>
              ))}
            </ul>

            {/* The whole point of this dialog. */}
            <p className="text-[13px] text-white/60 leading-relaxed mb-5 p-3 rounded-xl bg-sunny/[.08] border border-sunny/20">
              Ao tocar em <strong className="text-white">Ativar</strong>, o navegador vai pedir
              sua permissão. Escolha <strong className="text-white">Permitir</strong> — se
              recusar, ele não pergunta de novo e só dá para liberar nos ajustes do aparelho.
            </p>

            <div className="flex gap-2">
              <Button onClick={onClose} variant="ghost" fullWidth disabled={status === 'working'}>
                Agora não
              </Button>
              <Button onClick={handleSubscribe} fullWidth isLoading={status === 'working'}>
                Ativar
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  )
}
